import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faArrowTrendDown } from '@fortawesome/pro-solid-svg-icons/faArrowTrendDown';
import { faArrowTrendUp } from '@fortawesome/pro-solid-svg-icons/faArrowTrendUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { MprcCard, MprcContainer, MprcText } from 'components';
import { MprcCardFilterBox } from 'components/filter/MprcCardFilterBox';
import { IMenuItem, MprcDropdown } from 'components/MprcDropdown';
import { TrainingIndexEvolutionDto, TrainingIndexEvolutionItemDto } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashLabels } from 'views/dashboard/components/Charts';
import { DashLine } from 'views/dashboard/components/Charts/DashLine';
import { DashFooterExport } from 'views/dashboard/components/DashFooterExport';


export interface DashEvolutionLabel extends DashLabels {
    value: number;
}

type TrendingIndex = "none" | "up" | "down" | "stable";

interface IProps {
    currentMonth?: number;
    viewData: TrainingIndexEvolutionDto;
    aggregate?: boolean;
    aggregateSwitchRender?: React.ReactNode;
    cardHeader: (data?: TrainingIndexEvolutionItemDto) => React.ReactNode;
    onChangeMonth: (newMonth: number) => void;
    onExportData?: () => void;
}

const getLabels = (data: TrainingIndexEvolutionItemDto[]) => {

    var year = 0;

    return data.map((item, index) => {
        var day = moment(item.yearMonth, "YYYYMM").toDate();
        var mDay = moment(day);

        if (index == 0 || year! - day.getFullYear()) {
            year = day.getFullYear();
            return { shortLabel: mDay.format("MMM/YY"), longLabel: mDay.format("MMMM/YYYY"), value: data[index].yearMonth };
        } else {
            return { shortLabel: mDay.format("MMM"), longLabel: mDay.format("MMMM/YYYY"), value: data[index].yearMonth };
        }
    });
};


export const TrainingIndexEvolution: React.FC<IProps> =
    ({ cardHeader, viewData, onChangeMonth, aggregate = false, aggregateSwitchRender, onExportData, ...props }) => {

        const theme = useTheme();
        const { t } = useTranslation("dashboard");

        const [currentMonth, setCurrentMonth] = useState<number | undefined>(props.currentMonth);

        const data = aggregate ? viewData.aggregateData : viewData.nonAggregateData;

        const labels = getLabels(data);
        const currentData = data.find(p => p.yearMonth == currentMonth);
        const currenMonthLabel = labels.find(p => p.value == currentMonth)?.longLabel;

        const monthIndexTrending = useMemo((): TrendingIndex => {
            const currentMonthDataIndex = data.findIndex(p => p.yearMonth == currentMonth);
            if (currentMonthDataIndex > 0) {
                const currentMonthData = data[currentMonthDataIndex];
                const previusMonthData = data[currentMonthDataIndex - 1];

                if (previusMonthData.trainingIndex == null) return "none";

                if ((currentMonthData?.trainingIndex ?? 0) > (previusMonthData?.trainingIndex ?? 0))
                    return "up";
                else if ((currentMonthData?.trainingIndex ?? 0) < (previusMonthData?.trainingIndex ?? 0))
                    return "down";

                return "stable";
            }

            return "none";
        }, [data, currentMonth, currenMonthLabel]);

        const RenderFooter = (
            <Stack alignItems="center" spacing={1}>
                <Typography variant="caption" component="p">{t("TrainingIndex.EvolutionChartHint")}</Typography>
                {viewData.lastProcessingDate && <Typography variant="caption" component="p">{t("TrainingIndex.LastProcessingText", { date: Formaters.momentFullDateTime(viewData.lastProcessingDate, false, true) })}</Typography>}
            </Stack>
        );

        //Inicializa mes/ano atual, caso não tenha sido espeficicado
        useEffect(() => {
            if (!props.currentMonth) {
                var initCurrentMonth = viewData.nonAggregateData.length > 0 ? viewData.nonAggregateData[viewData.nonAggregateData.length - 1].yearMonth : undefined;
                if (initCurrentMonth) {
                    setCurrentMonth(initCurrentMonth);
                    onChangeMonth(initCurrentMonth);
                }
            }
            else {
                setCurrentMonth(props.currentMonth);
            }
        }, [props.currentMonth]);

        return (
            <MprcCard>
                <MprcContainer margin="dense">
                    <MprcCardFilterBox>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" px={theme => theme.spacing(2)} py={theme => theme.spacing(1)}>
                            <Box>
                                {cardHeader(currentData)}
                            </Box>
                            <Box>
                                <Stack direction="column" alignItems="center">
                                    <Typography variant="h1" fontSize={48}>{Formaters.localeNumber(currentData?.trainingIndex ?? 0)}%</Typography>
                                    {aggregateSwitchRender}
                                </Stack>
                            </Box>
                            <Box width={150}>
                                {currenMonthLabel &&
                                    <Stack direction="column" alignItems="center">
                                        {monthIndexTrending == "up" ?
                                            <MprcText color="success">
                                                <FontAwesomeIcon fixedWidth icon={faArrowTrendUp} fontSize={40} />
                                            </MprcText>
                                            : monthIndexTrending == "down" ?
                                                <MprcText color="error">
                                                    <FontAwesomeIcon fixedWidth icon={faArrowTrendDown} fontSize={40} />
                                                </MprcText>
                                                : <MprcText color="primary">
                                                    <FontAwesomeIcon className={monthIndexTrending == "none" ? "invisible" : ""} fixedWidth icon={faArrowRight} fontSize={40} />
                                                </MprcText>
                                        }
                                        <MprcDropdown
                                            text={currenMonthLabel}
                                            title={currenMonthLabel}
                                            items={labels.map(item => ({ text: item.longLabel, value: item.value } as IMenuItem)).reverse()}
                                            onChange={(selectedValue) => onChangeMonth(selectedValue)}
                                        />
                                    </Stack>
                                }
                            </Box>
                        </Stack>
                    </MprcCardFilterBox>
                </MprcContainer>
                <CardContent>
                    <MprcContainer margin="dense">
                        <Typography variant="overline">{t("TrainingIndex.EvolutionChartTitle")}</Typography>
                    </MprcContainer>
                    <MprcContainer>
                        <DashLine
                            datasets={[
                                {
                                    data: data.map(p => p.trainingIndex),
                                    backgroundColor: data.map(p => p.yearMonth == currentMonth ? theme.palette.warning.main : theme.palette.common.white),
                                    borderWidth: 2,
                                    borderColor: theme.palette.primary.main,
                                    tension: 0.2,
                                    pointBorderWidth: 1,
                                }
                            ]}
                            labels={labels}
                            enableTooltip
                            height={300}
                            valueFormat="percentage"
                            onClickDrillDown={(index) => onChangeMonth(labels[index].value)}
                        />
                    </MprcContainer>
                    {onExportData
                        ? <DashFooterExport onExportData={onExportData}>
                            {RenderFooter}
                        </DashFooterExport>
                        : RenderFooter}
                </CardContent>
            </MprcCard>
        );
    };
